'use client'

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ComponentData, SystemData, FailureScenario } from '@/lib/systemData'

interface SystemDiagramProps {
  system: SystemData
  onComponentClick: (componentId: string) => void
  failureScenario: FailureScenario | null
}

interface Connection {
  from: ComponentData
  to: ComponentData
}

export default function SystemDiagram({ system, onComponentClick, failureScenario }: SystemDiagramProps) {
  const [hoveredComponent, setHoveredComponent] = useState<string | null>(null)

  // Build connection map with bidirectional detection
  const connections = useMemo(() => {
    const conns: Connection[] = []
    const processed = new Set<string>()

    system.components.forEach((comp) => {
      comp.connectedTo.forEach((targetId) => {
        const target = system.components.find(c => c.id === targetId)
        if (target) {
          const pairKey = [comp.id, targetId].sort().join('-')

          // Check if this is a bidirectional connection
          const isBidirectional = target.connectedTo.includes(comp.id)

          if (!processed.has(pairKey)) {
            conns.push({ from: comp, to: target })
            if (isBidirectional) {
              // Mark as processed so we don't add the reverse separately
              processed.add(pairKey)
              // Add the reverse connection
              conns.push({ from: target, to: comp })
            }
          }
        }
      })
    })
    return conns
  }, [system.components])

  // Determine if a component is connected to the hovered one
  const isConnected = (componentId: string) => {
    if (!hoveredComponent) return false
    if (componentId === hoveredComponent) return true

    return connections.some(
      conn =>
        (conn.from.id === hoveredComponent && conn.to.id === componentId) ||
        (conn.to.id === hoveredComponent && conn.from.id === componentId)
    )
  }

  // Determine if a connection should be highlighted
  const isConnectionHighlighted = (conn: Connection) => {
    if (!hoveredComponent) return false
    return conn.from.id === hoveredComponent || conn.to.id === hoveredComponent
  }

  // Check if component is affected by failure scenario
  const isAffectedByFailure = (componentId: string) => {
    return failureScenario && failureScenario.affectedComponents.includes(componentId)
  }

  return (
    <div className="relative w-full" style={{ minHeight: '400px' }}>
      <svg
        viewBox="0 0 100 100"
        className="w-full h-auto block mx-auto"
        style={{ minHeight: '400px', maxHeight: '550px', marginLeft: '-15%' }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Connection lines */}
        <g fill="none">
          {connections.map((conn, index) => {
            const isHighlighted = isConnectionHighlighted(conn)
            const isUnrelated = hoveredComponent && !isHighlighted
            const isBroken = failureScenario && (
              isAffectedByFailure(conn.from.id) || isAffectedByFailure(conn.to.id)
            )

            // Check if this is part of a bidirectional pair
            const reverseExists = connections.some(
              c => c.from.id === conn.to.id && c.to.id === conn.from.id
            )

            // Offset for bidirectional arrows (side-by-side)
            const offset = reverseExists ? 1.5 : 0
            const isFirstDirection = reverseExists && conn.from.id < conn.to.id

            // Connection from bottom of source to top of destination
            // Offset horizontally if bidirectional
            const x1 = conn.from.x + conn.from.width / 2 + (isFirstDirection ? -offset : offset)
            const y1 = conn.from.y + conn.from.height  // Bottom of source
            const x2 = conn.to.x + conn.to.width / 2 + (isFirstDirection ? -offset : offset)
            const y2 = conn.to.y  // Top of destination

            return (
              <motion.line
                key={`${conn.from.id}-${conn.to.id}-${index}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{
                  pathLength: 1,
                  opacity: isUnrelated ? 0.15 : 1,
                  strokeWidth: isHighlighted ? 0.8 : isBroken ? 0.4 : 0.5,
                }}
                transition={{
                  pathLength: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                  opacity: { duration: 0.22, ease: 'easeOut' },
                  strokeWidth: { duration: 0.22, ease: 'easeOut' }
                }}
                stroke={
                  isBroken
                    ? 'rgba(239, 68, 68, 0.5)'
                    : isHighlighted
                    ? 'url(#connectionGradient)'
                    : 'rgba(255, 255, 255, 0.3)'
                }
                strokeDasharray={isBroken ? '2 2' : undefined}
                strokeLinecap="round"
              />
            )
          })}
        </g>

        {/* Gradient definition for highlighted connections */}
        <defs>
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(79, 209, 197, 0.8)" />
            <stop offset="100%" stopColor="rgba(79, 209, 197, 0.4)" />
          </linearGradient>
        </defs>

        {/* Animated particles along connections (only when hovering) */}
        <AnimatePresence>
          {hoveredComponent && connections
            .filter(conn => isConnectionHighlighted(conn))
            .map((conn, index) => {
              // Check if bidirectional for offset
              const reverseExists = connections.some(
                c => c.from.id === conn.to.id && c.to.id === conn.from.id
              )
              const offset = reverseExists ? 1.5 : 0
              const isFirstDirection = reverseExists && conn.from.id < conn.to.id

              const x1 = conn.from.x + conn.from.width / 2 + (isFirstDirection ? -offset : offset)
              const y1 = conn.from.y + conn.from.height
              const x2 = conn.to.x + conn.to.width / 2 + (isFirstDirection ? -offset : offset)
              const y2 = conn.to.y

              return (
                <motion.circle
                  key={`particle-${conn.from.id}-${conn.to.id}-${index}`}
                  r="0.8"
                  fill="rgba(79, 209, 197, 0.8)"
                  initial={{ cx: x1, cy: y1, opacity: 0 }}
                  animate={{
                    cx: [x1, x2],
                    cy: [y1, y2],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 2,
                    ease: 'linear',
                    repeat: Infinity,
                    delay: index * 0.3
                  }}
                />
              )
            })}
        </AnimatePresence>

        {/* Components */}
        {system.components.map((comp) => {
          const isHovered = hoveredComponent === comp.id
          const isRelated = isConnected(comp.id)
          const isUnrelated = hoveredComponent && !isRelated
          const isPrimary = comp.isPrimary ?? false
          const isFailureAffected = isAffectedByFailure(comp.id)

          return (
            <g key={comp.id}>
              {/* Glow effect when hovered */}
              <AnimatePresence>
                {isHovered && (
                  <motion.rect
                    x={comp.x}
                    y={comp.y}
                    width={comp.width}
                    height={comp.height}
                    fill="rgba(79, 209, 197, 0.3)"
                    rx="1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="blur-md"
                  />
                )}
              </AnimatePresence>

              {/* Main component rectangle */}
              <motion.rect
                x={comp.x}
                y={comp.y}
                width={comp.width}
                height={comp.height}
                fill={
                  isFailureAffected
                    ? 'rgba(239, 68, 68, 0.15)'
                    : isHovered
                    ? 'rgba(79, 209, 197, 0.2)'
                    : 'rgba(255, 255, 255, 0.08)'
                }
                stroke={
                  isFailureAffected
                    ? 'rgba(239, 68, 68, 0.8)'
                    : isHovered
                    ? 'rgba(79, 209, 197, 0.8)'
                    : isPrimary
                    ? 'rgba(255, 255, 255, 0.5)'
                    : 'rgba(255, 255, 255, 0.3)'
                }
                strokeWidth={isHovered ? '0.6' : isPrimary ? '0.5' : '0.4'}
                rx="1"
                className="cursor-pointer"
                initial={{ scale: 1, opacity: 1 }}
                animate={{
                  scale: isHovered ? 1.05 : 1,
                  opacity: isFailureAffected ? [1, 0.5, 1] : isUnrelated ? 0.3 : 1,
                }}
                transition={{
                  scale: { duration: 0.22, ease: 'easeOut' },
                  opacity: isFailureAffected
                    ? { duration: 1.5, repeat: Infinity, ease: 'easeInOut' }
                    : { duration: 0.22, ease: 'easeOut' }
                }}
                onMouseEnter={() => setHoveredComponent(comp.id)}
                onMouseLeave={() => setHoveredComponent(null)}
                onClick={() => onComponentClick(comp.id)}
                onTouchStart={() => onComponentClick(comp.id)}
              />

              {/* Component label */}
              <motion.text
                x={comp.x + comp.width / 2}
                y={comp.y + comp.height / 2 + 1}
                textAnchor="middle"
                fill={
                  isFailureAffected
                    ? 'rgba(239, 68, 68, 1)'
                    : isHovered
                    ? 'var(--accent-primary)'
                    : isPrimary
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(255, 255, 255, 0.7)'
                }
                fontSize="2.2"
                fontFamily="var(--font-mono)"
                className="pointer-events-none select-none"
                animate={{
                  opacity: isUnrelated ? 0.4 : 1,
                  fontWeight: isHovered ? '500' : isPrimary ? '450' : '400'
                }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
              >
                {comp.label}
              </motion.text>
            </g>
          )
        })}
      </svg>

      {/* Failure scenario indicator */}
      <AnimatePresence>
        {failureScenario && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="absolute top-4 left-4 right-4 bg-red-500/10 border border-red-500/30 rounded-sm p-3"
          >
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-1 animate-pulse"></div>
              <div className="flex-1">
                <p className="text-sm text-red-400 font-mono mb-1">
                  {failureScenario.name}
                </p>
                <p className="text-xs text-red-300/80">
                  {failureScenario.description}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
